import React, { useEffect, useMemo } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAppStore } from '@/store/useAppStore';
import { checkTokenFormat, getReservation, getSiteStatus } from '@/lib/appwrite';
import { parseUrlParams, applyBrandTheme } from '@/lib/utils';
import Loading from '@/components/Loading';
import ErrorView from '@/components/ErrorView';
import LinkUsedView from '@/components/LinkUsedView';
import { Toaster } from '@/components/ui/toaster';
import PoweredBy from '@/components/PoweredBy';

const WidgetPage = React.lazy(() => import('@/pages/widget/WidgetPage'));

const Dashboard          = React.lazy(() => import('@/pages/Dashboard'));
const ReservationSummary = React.lazy(() => import('@/pages/ReservationSummary'));
const GuestUpload        = React.lazy(() => import('@/pages/GuestUpload'));
const GuestReview        = React.lazy(() => import('@/pages/GuestReview'));
const PrivacyConsent     = React.lazy(() => import('@/pages/PrivacyConsent'));
const Confirmation       = React.lazy(() => import('@/pages/Confirmation'));

const AppRoutes: React.FC = () => {
  const {
    currentView,
    error,
    setView,
    setToken,
    setReservation,
    setProperty,
    setGuests,
    setError,
    setLoading,
    setMaintenanceEnabled,
    setMaintenanceMessage,
  } = useAppStore();

  const location = useLocation();

  // Parse URL params once per navigation, not on every render.
  const { reservationId, token } = useMemo(() => parseUrlParams(), [location.pathname, location.search]);

  useEffect(() => {
    if (!reservationId) {
      setError('No reservation ID in URL');
      setView('error');
      return;
    }

    const init = async () => {
      try {
        setLoading(true);

        if (!token) {
          throw new Error("Token mancante nell'URL");
        }

        // Store the token so GuestUpload / PrivacyConsent pages can use it for function calls.
        setToken(token);

        // Strip the token from the URL to prevent it leaking via browser history or referrer headers.
        window.history.replaceState(null, '', `/dashboard/${reservationId}`);

        checkTokenFormat(token);

        // Parallel: reservation data + site maintenance status from SiteStatus.
        const [
          { reservation, property, guests, tokenUsed },
          { maintenanceEnabled, maintenanceMessage },
        ] = await Promise.all([
          getReservation(reservationId, token),
          getSiteStatus(),
        ]);

        // Link already used: all documents were submitted and the token is
        // disabled server-side — show the warning screen, nothing else.
        if (tokenUsed) {
          setView('linkUsed');
          return;
        }

        setReservation(reservation);
        setProperty(property);
        if (property?.brandColors) {
          applyBrandTheme(property.brandColors);
        }

        setGuests(
          guests.length > 0
            ? guests
            : Array(reservation.guestCount).fill(null)
        );
        setMaintenanceEnabled(maintenanceEnabled);
        setMaintenanceMessage(maintenanceMessage);

        setView(reservation.status === 'completed' ? 'confirmation' : 'dashboard');
      } catch (err) {
        console.error('Init error:', err);
        setError(err instanceof Error ? err.message : 'Errore di inizializzazione');
        setView('error');
      } finally {
        setLoading(false);
      }
    };

    init();
  }, [reservationId, token, setView, setToken, setReservation, setProperty, setGuests, setError, setLoading, setMaintenanceEnabled, setMaintenanceMessage]);

  if (currentView === 'loading') return <Loading />;
  if (currentView === 'error') return <ErrorView message={error ?? undefined} />;
  if (currentView === 'linkUsed') return <LinkUsedView />;

  return (
    <>
      <React.Suspense fallback={<Loading />}>
        <Routes>
          <Route path="/" element={<Navigate to={`/dashboard/${reservationId}${location.search}`} replace />} />
          <Route path="/dashboard/:reservationId" element={<Dashboard />} />
          <Route path="/summary/:reservationId" element={<ReservationSummary />} />
          <Route path="/upload/:reservationId" element={<GuestUpload />} />
          <Route path="/review/:reservationId" element={<GuestReview />} />
          <Route path="/consent/:reservationId" element={<PrivacyConsent />} />
          <Route path="/confirmation/:reservationId" element={<Confirmation />} />
          <Route path="*" element={<Navigate to={`/dashboard/${reservationId}${token ? `?t=${token}` : ''}`} replace />} />
        </Routes>
      </React.Suspense>
      <PoweredBy />
      <Toaster />
    </>
  );
};

// RootRouter dispatches /widget to the public booking widget before
// AppRoutes runs its hooks — avoids conditional hook ordering issues.
const RootRouter: React.FC = () => {
  const location = useLocation();

  if (location.pathname.startsWith('/widget')) {
    return (
      <React.Suspense fallback={<Loading />}>
        <WidgetPage />
      </React.Suspense>
    );
  }

  return <AppRoutes />;
};

export default RootRouter;
